import { ChangeEvent, useEffect, useState } from "react";
import * as THREE from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { clone } from "three/examples/jsm/utils/SkeletonUtils";
import SceneInit from "../services/SceneInitialization";
import { View3DProps } from "../interfaces/View3D";
import { Alert, Spinner } from "react-bootstrap";
import { SelectOption } from "../interfaces/Toolbar";
import Select from "./Select";
import { DanceCouple, Limb, Position } from "../interfaces/Choreography";
import { lightColor, displacePartner, calculateSeparatedPosition, calculateCouplePositionGentleman, calculateCouplePositionLady, calculateCoupleRotationGentleman, calculateCoupleRotationLady, calculateSeparatedRotation} from "../services/VisServices";
import { coupleColors } from "../services/CoupleColors";

/**
 * Traverses the given model and sets the new color for each instance
 * @param model THREE.Group data structure in which the Miaxamo model is stored
 * @param newColorSurface new color for each surface mesh (given as a hex number in the format 0xRRGGBB)
 * @param newColorJoint new color for each joint mesh (given as a hex number in the format 0xRRGGBB)
 */
function setModelColor(model: THREE.Group | THREE.Object3D, newColor: string) {

  var newColorJoint = Number(
    parseInt(
      lightColor(String(newColor), -0.5).slice(1),
      16
    )
  );
  var newColorSurface = Number(
    parseInt(
      lightColor(String(newColor), 0.2).slice(1),
      16
    )
  );


  // Iterate through each child of the group
  model.traverse(function (child) {
    if (child instanceof THREE.Mesh) {
      // Change the color of the child Mesh (light color for surface, darker color for joint)
      if (child.name === "Alpha_Surface" || child.name === "Beta_Surface") {
        child.material.color.setHex(newColorSurface);
      }else {
        child.material.color.setHex(newColorJoint);
      }
    }
  });
}

/**
 * Searches through the array of all models and returns the index of the model in the models array
 * that matches the given uuid.
 *
 * @param models Array of models
 * @param uuidToFind model uuid to find (string-value)
 * @param Number of models to search through
 *
 * @returns The index of the model that has uuidToFind as an attribute value for uuid
 */
function findModelByUuid(
  models: THREE.Object3D[],
  uuidToFind: string,
  numModels: number
) {
  var modelIdToReturn: number = 0;
  var breakLoop = false;
  for (var i = 1; i <= numModels; i++) {
    if (!models[i]) continue;
    const model = models[i];
    model.traverse(function (child) {
      if (child instanceof THREE.Mesh) {
        if (child.uuid == uuidToFind) {
          modelIdToReturn = i;
          breakLoop = true;
          return;
        }
      }
    });
    if (breakLoop) break;
  }

  return modelIdToReturn;
}

function View3D(props: View3DProps) {
  //Array of all models: Indexing starts at 1 !!! Then the male and female models are stored
  //in an alternating fashion
  const [models, setModels] = useState<null | THREE.Object3D[]>(null);

  const [loadingFinished, setLoadingFinished] = useState(false);
  const [scene, setScene] = useState<SceneInit | null>(null);

  var Loaders = {
    FBX: new FBXLoader(),
  };

  useEffect(() => {
    const localScene = new SceneInit("3d-view-canvas");
    localScene.initialize();
    //test.animate();
    setScene(localScene);

    //Default camera position
    localScene.camera!.position.z = 1600;
    localScene.camera!.position.y = 600;
    localScene.camera!.position.x = 0;
    localScene.camera!.lookAt(new THREE.Vector3(0, 0, 0));

    let loadedModels: THREE.Object3D[] = [];

    //Load the two model files and copy them to boost performance
    for (let i = 1; i <= 2; i++) {
      let path = "../ybot.fbx";
      if (i === 2) {
        path = "../xbot.fbx";
      }
      Loaders.FBX.load(
        path,
        (model) => {
          
          if (i === 1) {
            setModelColor(model, String(props.gentlemanColor));
          } else {
            setModelColor(model, String(props.ladyColor));
          }

          copyModels(model, i, loadedModels, localScene.scene!);
        },
        (e) => {
          setLoadingFinished(true);
        }
      );
    }

    setModels(loadedModels);
  }, []);

  /**
   * Changes the color of all the gentlemen models to the user-defined props.gentlemanColor
   */
  function changeGentlemenColor() {
    if (scene && models) {
      for (let i = 1; i <= props.choreo.numCouples * 2; i += 2) {
        setModelColor(models[i], String(props.gentlemanColor));
      }
    }
  }

  /**
   * Changes the color of all the ladies models to the user-defined props.ladyColor
   */
  function changeLadiesColor() {
    if (scene && models) {
      for (let i = 2; i <= props.choreo.numCouples * 2; i += 2) {
        setModelColor(models[i], String(props.ladyColor));
      }
    }
  }

  /**
   * Callback function when the user-defined props.gentlemanColor gets changed
   */
  useEffect(() => {
    if (scene && models) {
      changeGentlemenColor();
    }
  }, [props.gentlemanColor]);

  /**
   * Callback function when the user-defined props.ladyColor gets changed
   */
  useEffect(() => {
    if (scene && models) {
      changeLadiesColor();
    }
  }, [props.ladyColor]);


  useEffect(() => {
    if (scene && models) {
      for (let i = 1; i <= 8; i++) {
        if (i <= props.choreo.numCouples) {
          models[2 * i - 1].visible = true;
          models[2 * i].visible = true;
        } else {
          models[2 * i - 1].visible = false;
          models[2 * i].visible = false;
        }
      }
    }
  }, [props.choreo.numCouples]);

  function copyModels(
    original: THREE.Group,
    maleOrFemale: number,
    loadedModels: THREE.Object3D[],
    scene: THREE.Scene
  ) {
    const startValue = maleOrFemale % 2 === 0 ? 2 : 1;
    for (let i = startValue; i <= props.choreo.numCouples * 2; i += 2) {
      const coupleNumber: number = Math.floor((i - 1) / 2);
      const danceCouple: DanceCouple =
        props.choreo.patterns[props.selectedPattern].positions[coupleNumber];
      const localId: number = (i - 1) % 2;
      const dancer = danceCouple.position[localId];
      const partner = danceCouple.position[localId === 0 ? 1 : 0];
      

      let model = clone(original) as THREE.Object3D;
      
      
      const head = model.getObjectByName("mixamorigHead");
      
      let pos: [number, number, number] = [0,0,0];
      let rotation: number = 0;
      if (danceCouple.joint) {
        if (localId == 0) {
          pos = calculateCouplePositionGentleman(danceCouple);
          rotation = calculateCoupleRotationGentleman(danceCouple);
        }else {
          pos = calculateCouplePositionLady(danceCouple);
          rotation = calculateCoupleRotationLady(danceCouple);
        }
      }else {
        pos = calculateSeparatedPosition(dancer);
        rotation = calculateSeparatedRotation(dancer);
      }
      model.scale.set(1, 1, 1);
      model.rotation.y = rotation;
      head!.rotation.y =
      ((dancer.bodyOrientation - dancer.headOrientation) * Math.PI) / 180;

      model.position.set(pos[0], pos[1], pos[2]);

      


      if (dancer.pose.limbs.length > 0) {
        dancer.pose.limbs.forEach((limb) => {
          initLimb(limb, model);
        });
      } else {
        if (danceCouple.joint && Math.abs(dancer.bodyOrientation - partner.bodyOrientation) === 180) {
          props.choreo.savedPoses[localId+1].limbs.forEach((limb) =>
            initLimb(limb, model)
          );
        }else {
          props.choreo.savedPoses[0].limbs.forEach((limb) =>
            initLimb(limb, model)
          );
        }
      }

      loadedModels[i] = model;
      scene.add(model);
    }
  }

  /**
   * Update the models!
   */
  useEffect(() => {
    if (models && props.show3DView && models[2]) {
      let i = 1;
      props.choreo.patterns[props.selectedPattern].positions.forEach(
        (danceCouple) => {
          const maleModel = models![2 * i - 1] as THREE.Group;
          const femaleModel = models![2 * i] as THREE.Group;
          if (danceCouple.joint) {
            updateCoupleModels(maleModel,femaleModel, danceCouple);
          } else {
            if (maleModel) {
              updateMaleModel(maleModel, i, danceCouple.position[0]);
            }
            if (femaleModel) {
              updateFemaleModel(femaleModel, i, danceCouple.position[1]);
            }
          }
          i++;
        }
      );
    }
  }, [props.selectedPattern, props.show3DView, props.choreo]);


  /**
   * Updates the position/orientation/poses of a couple. In the data model and the 2D visualization, gentleman
   * and lady of a couple have the same data. 
   * The current solution is that the gentleman takes the position from the data and the woman gets translated a bit facing
   * the gentleman.
   * If no custom pose was assigned, a default pose (basic dance posture) will be used.
   * 
   * @param maleModel 3D model of a gentlemanä
   * @param femaleModel  3D model of the corresponding lady
   * @param danceCouple Information about the couples position/orientation/poses from the choreography
   */
  function updateCoupleModels(
    maleModel: THREE.Group,
    femaleModel: THREE.Group,
    danceCouple: DanceCouple
  ) {

    //Both models should be visible
    maleModel.visible = true;
    femaleModel.visible = true;

    const malePos = calculateCouplePositionGentleman(danceCouple);
    maleModel.position.set(malePos[0], malePos[1], malePos[2]);

    const femalePos = calculateCouplePositionLady(danceCouple);
    femaleModel.position.set(femalePos[0], femalePos[1], femalePos[2]);

    //Body orientation of gentlemen and ladies (lady faces man and is therefore rotated by 180 degrees)
    const maleRotation = calculateCoupleRotationGentleman(danceCouple);
    const femaleRotation = calculateCoupleRotationLady(danceCouple);
    maleModel.rotation.y = maleRotation;
    femaleModel.rotation.y = femaleRotation;

    //Get head part of the models
    const maleHead = maleModel.getObjectByName("mixamorigHead");
    const femaleHead = femaleModel.getObjectByName("mixamorigHead");

    //Head rotation for gentlemen and ladies
    maleHead!.rotation.y =
      ((danceCouple.position[0].bodyOrientation - danceCouple.position[0].headOrientation) * Math.PI) / 180;
    femaleHead!.rotation.y =
      ((danceCouple.position[1].bodyOrientation - danceCouple.position[1].headOrientation) * Math.PI) / 180;

    //Load pose for the gentleman (case differentation: was a pose already assigned. If not use basic dance posture)
    if (danceCouple.position[0].pose.limbs.length > 0) {
      danceCouple.position[0].pose.limbs.forEach((limb) => {
        updateLimb(limb, 2*danceCouple.id-1);
      });
    } else if (Math.abs(danceCouple.position[0].bodyOrientation - danceCouple.position[1].bodyOrientation) === 180){
      props.choreo.savedPoses[1].limbs.forEach((limb) =>
        updateLimb(limb, 2*danceCouple.id-1)
      );
    }

    //Load pose for the lady
    if (danceCouple.position[1].pose.limbs.length > 0) {
      danceCouple.position[1].pose.limbs.forEach((limb) => {
        updateLimb(limb, 2*danceCouple.id);
      });
    } else if (Math.abs(danceCouple.position[0].bodyOrientation - danceCouple.position[1].bodyOrientation) === 180){
      props.choreo.savedPoses[2].limbs.forEach((limb) =>
        updateLimb(limb, 2*danceCouple.id)
      );
    }
  }


  /**
   * Sets the position and rotation of a male model 
   * @param femaleModel The 3D model
   * @param i model id
   * @param dancer information used to get the model orientation
   */
  function updateMaleModel(
    maleModel: THREE.Group,
    i: number,
    dancer: Position
  ) {
    const rotation: number = calculateSeparatedRotation(dancer);
    maleModel.rotation.y = rotation;
    const head = maleModel.getObjectByName("mixamorigHead");
    head!.rotation.y =
      ((dancer.bodyOrientation - dancer.headOrientation) * Math.PI) / 180;

    const pos = calculateSeparatedPosition(dancer);
    maleModel.position.set(pos[0], pos[1], pos[2]);

    const leftFoot = maleModel.getObjectByName("mixamorigLeftFoot");
    const rightFoot = maleModel.getObjectByName("mixamorigRightFoot");
  }

  /**
   * Sets the position and rotation of a female model 
   * @param femaleModel The 3D model
   * @param i model id
   * @param dancer information used to get the model orientation
   */
  function updateFemaleModel(
    femaleModel: THREE.Group,
    i: number,
    dancer: Position
  ) {
    femaleModel.visible = true;

    const rotation: number = calculateSeparatedRotation(dancer);
    femaleModel.rotation.y = rotation;
    const head = femaleModel.getObjectByName("mixamorigHead");
    head!.rotation.y =
      ((dancer.bodyOrientation - dancer.headOrientation) * Math.PI) / 180;

    const pos = calculateSeparatedPosition(dancer);
    femaleModel.position.set(pos[0], pos[1], pos[2]);
  }

  /**
   * Updates the marker position and the limbs of all 3D models in the scene in case
   *  - the 3D view is loaded
   *  - another pattern gets selected by the user
   *  - some data in the choreography changes (might not be too performant)
   */
  useEffect(() => {
    if (models && props.show3DView) {
      for (
        let dancerId = 1;
        dancerId <= props.choreo.numCouples * 2;
        dancerId++
      ) {
        const danceCouple = props.choreo.patterns[props.selectedPattern].positions[
          Math.floor((dancerId - 1) / 2)
        ];
        const localId = (dancerId - 1) % 2;
        const dancer = danceCouple.position[localId];
        const partner = danceCouple.position[localId === 0 ? 1 : 0];
        if (dancer.pose.limbs.length > 0) {
          dancer.pose.limbs.forEach((limb)=> {
            updateLimb(limb,dancerId);
          })
        }else {
          if (danceCouple.joint && Math.abs(dancer.bodyOrientation-partner.bodyOrientation)===180) {
            props.choreo.savedPoses[localId+1].limbs.forEach((limb) =>
              updateLimb(limb, dancerId)
            );
          }else {
            props.choreo.savedPoses[0].limbs.forEach((limb) =>
              updateLimb(limb, dancerId)
            );
          }
        }
      }
      updateMarker();
      updateGridHighlighting();
    }
  }, [props.choreo, props.selectedPattern, props.show3DView]);

  /**
   * Init limb rotation. Should be used after a pose information has been loaded from a choreo file.
   * Reads the limbs rotation from the limb parameter and adapts the 3D model accordingly.
   */
  function initLimb(limb: Limb, model: THREE.Object3D) {
    let modelLimb = model.getObjectByName(limb.name);
    modelLimb!.rotation.x = limb.rotation.x;
    modelLimb!.rotation.y = limb.rotation.y;
    modelLimb!.rotation.z = limb.rotation.z;
  }

  /**
   * Update limb rotation. Should be used after the initialization.
   * Reads the limbs rotation from the limb parameter and adapts the 3D model accordingly.
   */
  function updateLimb(limb: Limb, dancerId: number) {
    if (models && models[dancerId]) {
      let modelLimb = models![dancerId]!.getObjectByName(limb.name);
      modelLimb!.rotation.x = limb.rotation.x;
      modelLimb!.rotation.y = limb.rotation.y;
      modelLimb!.rotation.z = limb.rotation.z;
    }
  }

  /**
   * Updates the Dropdown-List above the 3D view in case the amount of couples has been changed.
   */
  useEffect(() => {
    let newSelectOptions = [];
    newSelectOptions.push({ value: 0, label: "None", key: "0" });
    for (let i = 0; i < props.choreo.numCouples; i++) {
      newSelectOptions.push({
        value: 2 * i + 1,
        label: `Gentleman ${i+1}`,
        key: String(2 * i + 1),
      });
      newSelectOptions.push({
        value: 2 * i + 2,
        label: `Lady ${i+1}`,
        key: String(2 * i + 2),
      });
    }
    setSelectDancerOptions(newSelectOptions);
  }, [props.choreo.numCouples]);

  const [selectDancerOptions, setSelectDancerOptions] = useState<
    SelectOption[]
  >([
    { value: 0, label: "None", key: "0" },
    { value: 1, label: "Gentleman 1", key: "1" },
    { value: 2, label: "Lady 1", key: "2" },
    { value: 3, label: "Gentleman 2", key: "3" },
    { value: 4, label: "Lady 2", key: "4" },
    { value: 5, label: "Gentleman 3", key: "5" },
    { value: 6, label: "Lady 3", key: "6" },
    { value: 7, label: "Gentleman 4", key: "7" },
    { value: 8, label: "Lady 4", key: "8" },
    { value: 9, label: "Gentleman 5", key: "9" },
    { value: 10, label: "Lady 5", key: "10" },
    { value: 11, label: "Gentleman 6", key: "11" },
    { value: 12, label: "Lady 6", key: "12" },
    { value: 13, label: "Gentleman 7", key: "13" },
    { value: 14, label: "Lady 7", key: "14" },
    { value: 15, label: "Gentleman 8", key: "15" },
    { value: 16, label: "Lady 8", key: "16" },
  ]);

  function onSelectDancerChange(e: ChangeEvent<HTMLSelectElement>) {
    const newlySelectedDancerId = +e.currentTarget.value;

    props.setSelectedDancerOption(newlySelectedDancerId);

    if (models && scene) {
      if (newlySelectedDancerId === 0) {
        scene.torus!.visible = false;
      } else {
        const coupleNumber: number = Math.floor(
          (newlySelectedDancerId - 1) / 2
        );
        const dancerNumberInDanceCouple: number =
          (newlySelectedDancerId - 1) % 2;
        scene.torus!.visible = true;
        const selectedDancerData =
          props.choreo.patterns[props.selectedPattern].positions[coupleNumber]
            .position[dancerNumberInDanceCouple];
        const markerX = selectedDancerData.x;
        const markerY = selectedDancerData.y;
        scene.torus!.position.set(markerX * 100, -70, -markerY * 100);
      }
    }
  }

  /**
   * The following code syncs the 3D visualization with the react state. 
   * Very dubious stuff I really do not understand. But it works and 
   * we therefore keep it and hope that it will not cause any problems in the future.
   */
  useEffect(() => {
    if (scene) {
      window.requestAnimationFrame(animate.bind(animate));

      render();
    }
  }, [scene]);

  let [animFrame, setAnimFrame] = useState<null | number>(null);
  function animate() {
    if (props.show3DView) {
      setAnimFrame(window.requestAnimationFrame(animate.bind(animate)));
    }
  }


  function updateGridHighlighting() {
      scene!.zeroBoxes.forEach(box => box.visible=(props.selectedDancerOption<=0));

      scene!.horizontalHighlighter!.visible = (props.selectedDancerOption>0);
      scene!.verticalHighlighter!.visible = (props.selectedDancerOption>0);

      

      if (props.selectedDancerOption > 0) {
        const pos = props.choreo.patterns[props.selectedPattern].positions[Math.floor((props.selectedDancerOption - 1) / 2)].position[(props.selectedDancerOption - 1) % 2];

        scene!.verticalHighlighter?.position.set(0,-100,-pos.y*100);
        scene!.horizontalHighlighter?.position.set(pos.x*100,-100,0);
      }

  }

  /**
   * Update the position and visiblity of the marker in case another dancer gets selected (or none)
   */
  useEffect(() => {
    if (scene && models) {
      updateMarker();
      updateGridHighlighting();
    }
  }, [props.selectedDancerOption]);

  /**
   * Enable or disable the grid (distances 1.0 and 0.5 metres + zero lines and outlines) depending on the user settings.
   */
  useEffect(() => {
    if (scene && models) {
      if (props.showGridIn3D) {
        scene.coarseGrid!.visible = true;
        scene.fineGrid!.visible = true;
        scene.zeroBoxes!.forEach(box => box.visible = true);
        scene.outlineBoxes!.forEach(box => box.visible = true);
      }else {
        scene.coarseGrid!.visible = false;
        scene.fineGrid!.visible = false;
        scene.zeroBoxes!.forEach(box => box.visible = false);
        scene.outlineBoxes!.forEach(box => box.visible = false);
      }

    }
  }, [props.showGridIn3D]);


  /**
   * Enable or disable the fine grid (0.5 metres) based on the user settings. If showGridIn3D is false and the
   * fine grid should be turned on, this operation will not do a thing.
   */
    useEffect(() => {
      if (scene && models) {
        if (props.fineGridEnabled === false) {
          scene.fineGrid!.visible = false;
        }else {
          if (props.showGridIn3D) {
            scene.fineGrid!.visible = true;
          }
        }
      }
    }, [props.fineGridEnabled]);

  /**
   * Sets the torus/marker position to the position of the selected model. Makes the marker visible.
   * Should only be called when props.selectedDancerOption gets changed.
   */
  function updateMarker() {
    if (props.selectedDancerOption <= 0) return;

    scene!.torus!.visible = true;

    const coupleId: number = Math.floor((props.selectedDancerOption - 1) / 2);
    let dancerId: number = (props.selectedDancerOption - 1) % 2;
    
    const couple = props.choreo.patterns[props.selectedPattern].positions[coupleId]
    const dancer = couple.position[dancerId];

    let pos: [number, number, number] = [0,0,0];
    if (couple.joint) {
      if (dancerId === 0) {
        pos = calculateCouplePositionGentleman(couple);
      }else {
        pos = calculateCouplePositionLady(couple);
      }
    }else {
      pos = calculateSeparatedPosition(dancer);
    }
    scene!.torus!.position.set(pos[0], pos[1], pos[2]);
  }

  useEffect(() => {
    if (scene && models) {
      
      //If the planes gets selected, the torus will become invisible
      if (scene.selectedUuid! === "plane") {
        props.setSelectedDancerOption(0);
        scene!.torus!.visible = false;
        //na is the signal to do nothing in the next animation frame
        scene.selectedUuid = "na";
      } else if (scene.selectedUuid !== "na") {
        //Find the id of the currently selected model
        const selectedModelId = findModelByUuid(
          models,
          scene.selectedUuid!,
          2 * props.choreo.numCouples
        );
        props.setSelectedDancerOption(selectedModelId);
        scene.selectedUuid = "na";
      }
    }

    render();
  }, [animFrame]);

  useEffect(() => {
    if (scene) {
      if (props.show3DView) {
        animate();
      }
    }
  }, [props.show3DView]);

  /**
   * Render callback function. 
   * Rotates the torus that marks the selected dancer and initializes the rendering pipeline.
   */
  function render() {
    if (scene && props.show3DView) {
      scene.torus!.rotation.z += 0.05;
      scene.renderer!.render(
        scene.scene as THREE.Scene,
        scene.camera as THREE.Camera
      );
    }
  }

  /**
   * Rotates the 3D camera consistently with the view on the 2D visualization. 
   * Gets called automatically if the view on the 2D-vis changes.
   */
  useEffect(() => {
    if (scene && models) {
      scene.camera!.lookAt(new THREE.Vector3(0,-100,0));
      switch (props.patternRotation) {
        case "Front":
          scene.camera!.position.set(0, 600, 1600);
          break;
        case "Right":
          scene.camera!.position.set(1600, 600, 0);
          break;
        case "Back":
          scene.camera!.position.set(0, 600, -1600);
          break;
        case "Left":
          scene.camera!.position.set(-1600, 600, 0);
          break;
      }
      scene.controls!.update();
    }
  }, [props.patternRotation]);

  return (
    <div
      id="3d-view-wrapper"
      style={{
        width: "100%",
        height: "100%",
        visibility: props.show3DView ? "visible" : "hidden",
      }}
    >
      {!loadingFinished && (
        <Alert key={"info"} variant={"info"}>
          <Spinner animation="border" className="me-2" size="sm" /> Loading
          models ...
        </Alert>
      )}
      <Select
        value={props.selectedDancerOption}
        onChange={onSelectDancerChange}
        options={selectDancerOptions}
        renderOnAllDevices={true}
      />

      <canvas id="3d-view-canvas" style={{ width: "100%", height: "90%" }} />
    </div>
  );
}

export default View3D;
